import { useEffect, useRef } from "react";

const FOUNDERS = [
  {
    id: "aakash",
    name: "Aakash Chaudhry",
    role: "Co-founder",
    tagline: "The Maverick of Modern Education",
    bio:
      "A leader in education, he co-founded Aakash Institute, shaping one of India's top test prep organizations. He also founded Plaksha University, supports Ashoka University, and has been recognized as a “Disruptive Leader” and a “40 Under 40” honouree.",
    image:
      "https://asset.sparkl.me/pb/assets/img/founderimages/aakash_chaudhry.png",
    accent: "#FFD8BF",
    tape: "#F8CC6B",
    rotate: -2.4,
    section: "Profile · Education",
    issue: "No. 01",
    chips: ["Aakash Institute", "Plaksha University", "40 Under 40"],
  },
  {
    id: "pavan",
    name: "Pavan Chauhan",
    role: "Co-founder",
    tagline: "The Edtech Pioneer & Education Enthusiast",
    bio:
      "A visionary in India's edtech space — founded a K-12 content company in 2005, created a 130-episode math series, and launched Meritnation in 2009, India's first major edtech platform, later acquired by Aakash Institute in 2020.",
    image:
      "https://asset.sparkl.me/pb/assets/img/founderimages/pavan_chauhan.png",
    accent: "#B9D8FF",
    tape: "#97D8C4",
    rotate: 1.8,
    section: "Profile · Edtech",
    issue: "No. 02",
    chips: ["Meritnation", "Edtech Pioneer", "K-12 Content"],
  },
  {
    id: "ritesh",
    name: "Ritesh Hemrajani",
    role: "Co-founder",
    tagline: "Trailblazer behind cutting-edge education solutions",
    bio:
      "A seasoned education entrepreneur with 28 years of experience, he co-founded Meritnation, one of India's first edtech startups. As co-founder of Sparkl, he's building an innovative digital product for international curricula.",
    image:
      "https://asset.sparkl.me/pb/assets/img/founderimages/ritesh_hemrajani.png",
    accent: "#97D8C4",
    tape: "#FFD8BF",
    rotate: -1.4,
    section: "Profile · Innovation",
    issue: "No. 03",
    chips: ["28 yrs experience", "Meritnation", "International Curricula"],
  },
];

export default function FoundersSection() {
  const headRef = useRef(null);
  const cardRefs = useRef([]);

  useEffect(() => {
    const els = [headRef.current, ...cardRefs.current].filter(Boolean);
    if (!("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("is-visible"));
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -40px 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <section className="sp-fd" id="founders" data-testid="founders-section">
      <div className="sp-fd-bg" aria-hidden="true" />
      <div className="sp-fd-grain" aria-hidden="true" />

      <div className="sp-fd-inner">
        {/* Editorial masthead */}
        <header ref={headRef} className="sp-fd-masthead" data-testid="founders-head">
          <div className="sp-fd-rule" aria-hidden="true" />
   <br />
          <h2 className="sp-fd-title">
            Three minds. <em>Thirty years.</em>{" "}
            <span className="sp-grad-word">One mission.</span>
          </h2>
          <p className="sp-fd-sub">
            By the founders of Aakash Institute &amp; Meritnation.com — bringing
            decades of expertise in education, they are shaping Sparkl to
            redefine personalized learning, academic excellence, and
            technology-driven education.
          </p>

          <div className="sp-fd-stats" data-testid="founders-stats">
            <div className="sp-fd-stat-col">
              <span className="sp-fd-stat-num">30+</span>
              <span className="sp-fd-stat-lbl">Years of Experience</span>
            </div>
            <div className="sp-fd-stat-divider" aria-hidden="true" />
            <div className="sp-fd-stat-col">
              <span className="sp-fd-stat-num">2</span>
              <span className="sp-fd-stat-lbl">Landmark EdTech Builds</span>
            </div>
            <div className="sp-fd-stat-divider" aria-hidden="true" />
            <div className="sp-fd-stat-col">
              <span className="sp-fd-stat-num">1</span>
              <span className="sp-fd-stat-lbl">Vision for Sparkl</span>
            </div>
          </div>

          <div className="sp-fd-rule" aria-hidden="true" />
        </header>

        {/* Magazine cuttings */}
        <div className="sp-fd-grid" data-testid="founders-grid">
          {FOUNDERS.map((f, i) => (
            <article
              key={f.id}
              ref={(el) => (cardRefs.current[i] = el)}
              className="sp-fd-clip"
              style={{
                "--accent": f.accent,
                "--tape": f.tape,
                "--rot": `${f.rotate}deg`,
                "--delay": `${i * 130 + 80}ms`,
              }}
              data-testid={`founder-card-${f.id}`}
            >
              {/* Washi tape */}
              <span className="sp-fd-tape" aria-hidden="true">
                <span className="sp-fd-tape-stripe" />
              </span>

              {/* Photo plate — sepia-tinted, like a magazine cutout */}
              <div className="sp-fd-photo">
                <img src={f.image} alt={`${f.name}, ${f.role} of Sparkl`} />
                <div className="sp-fd-photo-tint" aria-hidden="true" />
              </div>

              {/* Editorial dateline */}
              <div className="sp-fd-dateline-row">
                {/* <span className="sp-fd-section">{f.section}</span> */}
                {/* <span className="sp-fd-issue">{f.issue}</span> */}
              </div>

              {/* Name in serif headline */}
              <h3 className="sp-fd-name">{f.name}</h3>

              {/* Italic tagline pull-quote */}
              <p className="sp-fd-tagline">&ldquo;{f.tagline}&rdquo;</p>

              {/* Body with drop cap */}
              <p className="sp-fd-bio">
                <span className="sp-fd-dropcap">{f.bio.charAt(0)}</span>
                {f.bio.slice(1)}
              </p>

              {/* Chips */}
              <div className="sp-fd-chips">
                {f.chips.map((c) => (
                  <span key={c} className="sp-fd-chip">
                    {c}
                  </span>
                ))}
              </div>

              {/* Byline rule + role */}
              {/* <div className="sp-fd-byline">
                <span className="sp-fd-byline-rule" aria-hidden="true" />
                <span className="sp-fd-byline-text">{f.role} · Sparkl</span>
              </div> */}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
